/**
 * Frontend entry point.
 *
 * Routing: History API, clean URLs — /login, /register, /forgot-password,
 * /reset-password?token=..., /verify-email?token=..., /play (authenticated).
 *
 * Game developers wire their rendering engine inside mountGame() — replace
 * the placeholder with PixiJS, Babylon.js, or DOM rendering.
 */
import { bootstrapSession, logout } from './core/auth/auth-api';

// PWA: register the service worker (production only — the dev server serves
// modules the worker must never intercept). This is what upgrades the manifest
// from "bookmark" to a real installable app (CU-86cb58yuh item 16).
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => { /* install offer just won't appear */ });
  });
}
import { renderLogin } from './core/auth/login-screen';
import { renderRegister } from './core/auth/register-screen';
import { renderForgotPassword } from './core/auth/forgot-password-screen';
import { renderResetPassword } from './core/auth/reset-password-screen';
import { renderVerifyEmail } from './core/auth/verify-email-screen';
import { renderCheckEmail } from './core/auth/check-email-screen';
import { authStore } from './core/state/auth-store';
import { connectSocket, disconnectSocket } from './core/network/socket';
import { fetchSiteInfo, isPreviewBuild, stripBrandMeta } from './core/preview/preview';
import { mountGame } from './engine/bootstrap';

import { renderLanding } from './engine/landing/landing-screen';
import { renderLegalPage } from './engine/landing/legal-page';
import { attachVerifyRedirect } from './engine/verify-redirect';
import { shouldRouteInApp } from './engine/spa-link';
import { captureReferralCode } from './engine/game/referral-api';
import { installIdleLogout, sessionExpiredWhileAway, clearSeen } from './engine/game/idle-logout';
import { initLocale } from './engine/i18n';
import { installPrompt } from './engine/pwa-install';
import { setRates } from './engine/i18n/money';
import { fetchRates } from './engine/game/i18n-api';
import { initGoogleTags, trackPageView } from './engine/analytics/gtag';

// Referral links (`?ref=CODE`) can land on any pre-auth screen (landing,
// /login, /register) — capture + strip before the core auth UI ever renders;
// the actual claim happens later, once a session exists (see HeroScreen).
captureReferralCode();

const app = document.getElementById('app')!;
let teardown: (() => void) | null = null;

export function navigate(to: string) {
  if (location.pathname !== to) history.pushState(null, '', to);
  render();
}

/** Combine several teardown fns into one. */
const combine = (...fns: Array<(() => void) | null | undefined>) => () => fns.forEach((f) => f?.());

/**
 * Floating "back to the landing site" control shown over the core auth screens
 * (which the game can't edit). Lets a visitor who hit Log In / Sign Up bail back
 * to the marketing landing at `/`. Returns a teardown that removes it.
 */
function addBackToLanding(): () => void {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.setAttribute('aria-label', 'Back to home');
  btn.innerHTML = '<span aria-hidden="true">&#8249;</span> Back';
  // a hewn iron tag, not a glass pill — the pill was the last rounded control
  // on the auth screens (and .14em tracking was the art-deco tell)
  btn.style.cssText = [
    'position:fixed', 'top:20px', 'left:20px', 'z-index:1000',
    'display:inline-flex', 'align-items:center', 'gap:7px',
    'font:700 12px/1 "Cormorant SC", Georgia, serif',
    'letter-spacing:.08em', 'text-transform:uppercase',
    'color:#e8d9a8', 'background:linear-gradient(180deg,#1a2230,#0d1219 62%,#10151e)',
    'border:0', 'border-radius:0',
    'clip-path:polygon(6% 0%, 100% 4%, 96% 100%, 0% 92%)',
    'box-shadow:inset 0 1px 0 rgba(226,236,248,.10), inset 0 0 0 1px rgba(0,0,0,.55)',
    'padding:11px 20px', 'cursor:pointer', 'transition:background .2s, transform .15s',
  ].join(';');
  btn.onmouseenter = () => { btn.style.background = 'linear-gradient(180deg,#2a3448,#141b26 62%,#161d29)'; btn.style.transform = 'translateY(-1px)'; };
  btn.onmouseleave = () => { btn.style.background = 'linear-gradient(180deg,#1a2230,#0d1219 62%,#10151e)'; btn.style.transform = 'none'; };
  btn.onclick = () => navigate('/');
  document.body.appendChild(btn);
  return () => btn.remove();
}

/**
 * The token was just spent server-side, so any cached user still says
 * emailVerified:false — /play would bounce it straight to /check-email.
 * Re-bootstrap first so the store reflects the verification.
 */
function addVerifyRedirect(): () => void {
  return attachVerifyRedirect(document, async () => {
    const user = await bootstrapSession();
    navigate(user?.emailVerified ? '/play' : '/login');
  });
}

/**
 * The living harbour behind the core auth screens (whose markup the game can't
 * edit): a generated ambient loop — dock torches burning, aurora breathing,
 * snow falling — slotted as a game-owned element under the pane scrims.
 * Under prefers-reduced-motion nothing is injected and the painted webp in
 * core's ::before stands in. Returns a teardown that removes it.
 */
function addAuthLiveBackdrop(): () => void {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return () => {};
  const host = app.querySelector('.bg-auth-gradient');
  if (!host) return () => {};
  const wrap = document.createElement('div');
  wrap.className = 'auth-live';
  wrap.setAttribute('aria-hidden', 'true');
  const video = document.createElement('video');
  video.muted = true;
  video.loop = true;
  video.playsInline = true;
  video.autoplay = true;
  video.src = '/landing/live-login.mp4';
  wrap.appendChild(video);
  host.prepend(wrap);
  video.play()?.catch(() => {});
  return () => wrap.remove();
}

function render() {
  teardown?.();
  teardown = null;

  const path = location.pathname;
  const params = new URLSearchParams(location.search);
  const user = authStore.get().user;

  const afterAuth = () => {
    const u = authStore.get().user;
    if (u && !u.emailVerified) { navigate('/check-email'); return; }
    navigate('/play');
  };

  switch (path) {
    case '/login':
      if (user) { navigate(user.emailVerified ? '/play' : '/check-email'); return; }
      teardown = combine(renderLogin(app, afterAuth), addAuthLiveBackdrop(), addBackToLanding());
      break;
    case '/register':
      if (user) { navigate(user.emailVerified ? '/play' : '/check-email'); return; }
      teardown = combine(renderRegister(app, afterAuth), addAuthLiveBackdrop(), addBackToLanding());
      break;
    case '/forgot-password':
      if (user) { navigate('/play'); return; }
      teardown = combine(renderForgotPassword(app), addAuthLiveBackdrop(), addBackToLanding());
      break;
    case '/reset-password':
      teardown = combine(renderResetPassword(app, params.get('token') ?? '', () => navigate('/login')), addAuthLiveBackdrop());
      break;
    case '/verify-email':
      teardown = combine(renderVerifyEmail(app, params.get('token') ?? ''), addAuthLiveBackdrop(), addVerifyRedirect());
      break;
    case '/check-email':
      if (!user) { navigate('/login'); return; }
      teardown = combine(renderCheckEmail(app, user.email, () => { navigate('/login'); }), addAuthLiveBackdrop());
      break;
    case '/play':
    case '/':
      if (!user) {
        // First-time / logged-out visitors see the marketing landing at the root;
        // its "Play Free" CTAs hand off to the login screen.
        if (path === '/') { teardown = renderLanding(app, () => navigate('/login')); break; }
        navigate('/login');
        return;
      }
      if (!user.emailVerified) { navigate('/check-email'); return; }
      {
        // AWAIT the logout before routing: `render()` reads authStore, and with
        // the store still holding a user the /login case bounced straight back
        // to /play (remounting the whole game) until the POST happened to land.
        const endSession = () => { clearSeen(); void logout().then(() => navigate('/login')); };
        // Product rule: 10 minutes without input ends the session (and with it,
        // any chance of coming back to hours-stale in-memory game state).
        // The socket carries presence only (core marks a connected player online;
        // game/bots counts them into "In the hall"). Removed 2026-08-08 while /ws had
        // no gateway and every login logged failed dials; RealtimeGateway answers now.
        connectSocket();
        teardown = combine(mountGame(app, user, endSession), installIdleLogout(endSession), disconnectSocket);
      }
      break;
    case '/admin-panel/sandbox':
    case '/admin-panel':
      if (!user) { navigate('/login'); return; }
      if (user.role !== 'ADMIN' && user.role !== 'KYC_ADMIN') { navigate('/play'); return; }
      void import('./engine/admin/mount').then(({ mountAdmin }) => { teardown = mountAdmin(app, user); });
      break;
    default:
      // /legal/:slug — standalone CMS-driven legal document (Terms, Privacy, Cookies)
      if (path.startsWith('/legal/')) {
        teardown = renderLegalPage(app, decodeURIComponent(path.slice('/legal/'.length)), () => navigate('/'));
        break;
      }
      navigate('/login');
  }
  // Reached only by the screen that actually rendered: a redirect calls
  // navigate() (which tracks the target) and returns before this line.
  trackPageView(location.pathname + location.search);
}

window.addEventListener('popstate', render);

document.addEventListener('click', (e) => {
  const a = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null;
  if (!a || !shouldRouteInApp(a)) return;
  e.preventDefault();
  navigate(a.pathname + a.search);
});

(async () => {
  // Fetch site-info first — drives preview-mode gating throughout the app
  await fetchSiteInfo();
  if (isPreviewBuild()) stripBrandMeta();
  // Not awaited: analytics must never delay the first paint. A preview build
  // is a demo for third parties and reports nowhere.
  if (!isPreviewBuild()) void initGoogleTags();

  // Locale before the first paint, so a returning German visitor never sees a
  // flash of English. Awaited alongside the session rather than after it: the
  // pre-auth screens need the catalogue too, and neither depends on the other.
  // Display FX rates ride along; a failure there only means prices render in
  // the charge currency (see i18n/money.ts), so it must not block boot.
  await Promise.all([
    bootstrapSession(),
    initLocale(),
    fetchRates().then(setRates).catch(() => {}),
  ]);

  // The refresh cookie outlives the tab by days, so a session restored above is
  // only good if the player was actually here recently. A tab closed (or a
  // laptop asleep) for the idle timeout is a logged-out player — the same ten
  // minutes rule 1 enforces while the page is open. QA 86cb9yd3p item 2.
  if (authStore.get().user && sessionExpiredWhileAway()) {
    clearSeen();
    await logout();
  }

  render();

  // The install offer, armed once for the whole app rather than per screen: it
  // outlives routing (login → landing → /play) and no browser shows one by
  // itself any more. Mounted after the catalogue loads so its copy is already
  // in the player's tongue. See engine/pwa-install.ts.
  installPrompt();

  // NOTE: deliberately NO global re-render on locale change. The Vue tree
  // repaints reactively, and the landing refills its own copy in place (see
  // landing/lang-pick.ts) precisely so that switching tongue does not reset
  // scroll position or restart the ember/snow animations. A blanket render()
  // here would undo both.

  authStore.subscribe((s) => {
    if (!s.user && location.pathname === '/play') navigate('/login');
  });
})();

// NOTE: no beforeunload teardown. Browsers fire beforeunload for navigations
// that never happen (mailto: links hand off to the mail client and stay on the
// page) — tearing down there left a blank, dead SPA. On a real unload the
// browser discards the page anyway; nothing needs manual cleanup.
